import { useLazyQuery, useQuery } from '@apollo/client'

import { useEffect, useState } from 'react'

import { Toaster } from '@grupoboticario/flora-react'

import { INITIAL_EVENT_FORM_VALUES } from '@/constants/eventFormDefaults'
import { GET_BENEFIT_EVENTS_LIST } from '@/graphql/queries/benefitEvents'
import { GET_EVENT_BY_ID } from '@/graphql/queries/eventById'
import { mapApiDataToFormValues } from '@/utils/eventMappers/eventMappers'

/**
 * Hook to manage state and loading of event data.
 *
 * @param {object} params - Hook parameters.
 * @param {string} params.eventId - Event ID to be loaded.
 * @param {boolean} params.isNewEvent - Indicates if it is a new event.
 * @param {object} params.query - Router query object.
 * @param {function} params.push - Router push function.
 * @returns {object} { formValues, setFieldValue, isLoadingEvent, refreshEvent }
 */
export function useEventData({ eventId, isNewEvent }) {
  const [formValues, setFormValues] = useState(INITIAL_EVENT_FORM_VALUES)
  const [isLoadingEvent, setIsLoadingEvent] = useState(false)

  const { refetch: refetchBenefitEvents } = useQuery(GET_BENEFIT_EVENTS_LIST)

  const [getEventById] = useLazyQuery(GET_EVENT_BY_ID, {
    onCompleted: (data) => {
      if (data?.getEventById) {
        const mappedData = mapApiDataToFormValues(data.getEventById)
        setFormValues(mappedData)
      }
      setIsLoadingEvent(false)
    },
    onError: () => {
      Toaster.notify({
        kind: 'error',
        description: 'Erro ao carregar dados do evento. Por favor, tente novamente.'
      })
      setIsLoadingEvent(false)
    }
  })

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId || isNewEvent) return
      setIsLoadingEvent(true)
      await getEventById({ variables: { eventId } })
    }
    fetchEventData()
  }, [eventId, isNewEvent, getEventById])

  const setFieldValue = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const refreshEvent = async () => {
    if (eventId && !isNewEvent) {
      setIsLoadingEvent(true)
      await getEventById({ variables: { eventId } })
      await refetchBenefitEvents()
    }
  }

  return {
    formValues,
    setFieldValue,
    isLoadingEvent,
    refreshEvent
  }
}

export default useEventData
